import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { paletteFor } from '../lib/categoryPalette.js';

// Shared archive browser — the inner UI of the /archive page (article/edition
// toggle, filters, infinite-scroll results). Rendered directly by the route
// page and inside the editor dashboard's Archive modal, so the experience
// matches in both places.
//
// When mounted inside a scrollable container (e.g. a modal body), the
// infinite-scroll IntersectionObserver auto-detects the nearest scrolling
// ancestor and uses it as its root so "load more" triggers as the user
// scrolls within the modal rather than the page viewport.

function Chevron({ open }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
      aria-hidden="true"
    >
      <path d="M3 4.5L6 7.5L9 4.5" />
    </svg>
  );
}

const CATEGORIES = ['Unmanned', 'Manned', 'Airspace', 'AI', 'Manufacturing'];

const PAGE_SIZE = 30;

function formatPrettyDate(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatShortDate(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// Walk up the DOM from `el` to find the nearest scrollable ancestor. Returns
// null when the viewport is the closest scroll container — that's the
// signal IntersectionObserver expects for "use the document viewport".
function findScrollParent(el) {
  let node = el?.parentElement;
  while (node) {
    const style = window.getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(style.overflowY)) return node;
    node = node.parentElement;
  }
  return null;
}

function ArticleRow({ row }) {
  const [open, setOpen] = useState(false);
  const [imageOk, setImageOk] = useState(Boolean(row.imageUrl));
  const hasImage = Boolean(row.imageUrl) && imageOk;
  const sourceUrl =
    row.sourceUrl || row.source?.canonicalUrl || row.source?.url || null;
  const palette = paletteFor(row.category);

  return (
    <div
      className={[
        'group bg-white border border-hxd-border-soft rounded transition-all border-l-[3px]',
        open
          ? 'border-hxd-orange shadow-sm'
          : 'hover:border-hxd-orange hover:shadow-sm',
      ].join(' ')}
      style={{ borderLeftColor: palette.bar }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex gap-4 p-4 text-left"
      >
        {hasImage && (
          <div className="hidden sm:block shrink-0 w-32 h-24 rounded overflow-hidden bg-hxd-surface border border-hxd-border-soft">
            <img
              src={row.imageUrl}
              alt={row.imageAlt || ''}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={() => setImageOk(false)}
              onLoad={(e) => {
                const { naturalWidth, naturalHeight } = e.currentTarget;
                if (naturalWidth < 200 || naturalHeight < 120) setImageOk(false);
              }}
            />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-hxd-muted mb-1">
            {row.category && (
              <span
                className="font-display font-bold uppercase tracking-display-sm"
                style={{ color: paletteFor(row.category).ink }}
              >
                {row.category}
              </span>
            )}
            {row.source?.displayName && (
              <>
                <span>·</span>
                <span className="font-semibold text-[#333]">{row.source.displayName}</span>
              </>
            )}
            <span>·</span>
            <span>{formatShortDate(row.isoDate)}</span>
          </div>

          <h3 className="text-[15px] font-bold text-hxd-ink leading-tight m-0 group-hover:text-hxd-orange transition-colors">
            {row.headline || '(no headline)'}
          </h3>

          {!open && row.keyTakeaway && (
            <p
              className={[
                'text-[13px] text-[#222] mt-1.5 leading-relaxed m-0 font-medium',
                hasImage ? 'line-clamp-2' : 'line-clamp-3',
              ].join(' ')}
            >
              {row.keyTakeaway}
            </p>
          )}
          {!open && !row.keyTakeaway && row.summary && (
            <p
              className={[
                'text-[13px] text-hxd-muted mt-1.5 leading-relaxed m-0',
                hasImage ? 'line-clamp-2' : 'line-clamp-3',
              ].join(' ')}
            >
              {row.summary}
            </p>
          )}
        </div>

        <div className="shrink-0 self-start text-hxd-muted group-hover:text-hxd-orange mt-1">
          <Chevron open={open} />
        </div>
      </button>

      {open && (
        <div
          className={[
            'px-4 pb-3 -mt-2',
            !hasImage ? 'sm:pl-8' : '',
          ].join(' ')}
        >
          {row.keyTakeaway && (
            <p className="text-[13.5px] text-hxd-ink leading-snug m-0 font-medium">
              {row.keyTakeaway}
            </p>
          )}
          {row.summary && (
            <p
              className={`text-[13px] text-[#444] leading-snug m-0 ${
                row.keyTakeaway ? 'mt-1.5' : ''
              }`}
            >
              {row.summary}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-2.5 text-[12px]">
            {sourceUrl && (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-white bg-hxd-orange hover:bg-black px-3 py-1 rounded transition-colors"
              >
                Read full article →
              </a>
            )}
            <Link
              to={`/archive/${row.isoDate}`}
              onClick={(e) => e.stopPropagation()}
              className="text-hxd-orange font-semibold hover:underline"
            >
              View full edition →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function EditionCard({ row }) {
  const [imageOk, setImageOk] = useState(Boolean(row.heroImage?.url));
  const hasImage = Boolean(row.heroImage?.url) && imageOk;

  return (
    <Link
      to={`/archive/${row.isoDate}`}
      className="flex flex-col bg-white border border-hxd-border rounded overflow-hidden hover:shadow-lg hover:border-hxd-orange transition-all"
    >
      {hasImage && (
        <div className="aspect-[16/9] bg-hxd-surface overflow-hidden">
          <img
            src={row.heroImage.url}
            alt={row.heroImage.alt || ''}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setImageOk(false)}
          />
        </div>
      )}

      <div className="bg-black px-4 py-3">
        <div className="font-display text-[11px] font-bold tracking-display-sm uppercase text-hxd-orange">
          {formatPrettyDate(row.isoDate)}
        </div>
        <div className="font-display text-[12px] font-bold tracking-display-xs text-white mt-1">
          HXD Morning Brief
        </div>
      </div>

      <div className="px-4 py-3 flex-1">
        {row.introSummary ? (
          <p
            className={[
              'text-[12px] leading-relaxed text-[#444] m-0',
              hasImage ? 'line-clamp-5' : '',
            ].join(' ')}
          >
            {row.introSummary}
          </p>
        ) : (
          <p className="text-[12px] text-hxd-muted italic m-0">
            Red-thread summary unavailable.
          </p>
        )}
      </div>

      {row.categories && row.categories.length > 0 && (
        <div className="px-4 pb-2">
          <div className="text-[10px] font-bold uppercase tracking-display-xs text-hxd-muted mb-1">
            Categories
          </div>
          <div className="flex flex-wrap gap-1">
            {row.categories.map((c) => {
              const palette = paletteFor(c);
              return (
                <span
                  key={c}
                  className="font-display text-[10px] font-bold uppercase tracking-display-sm rounded-[2px] px-1.5 py-0.5"
                  style={{
                    color: palette.ink,
                    backgroundColor: palette.track,
                    border: `1px solid ${palette.bar}`,
                  }}
                >
                  {c}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {row.sources && row.sources.length > 0 && (
        <div className="px-4 pb-2">
          <div className="text-[10px] font-bold uppercase tracking-display-xs text-hxd-muted mb-1">
            Sources
          </div>
          <div className="text-[11px] text-[#444] leading-snug">
            {row.sources.slice(0, 8).join(' · ')}
            {row.sources.length > 8 && (
              <span className="text-hxd-muted"> +{row.sources.length - 8} more</span>
            )}
          </div>
        </div>
      )}

      <div className="px-4 pb-3 pt-2 flex items-center justify-between text-[11px] border-t border-hxd-border-soft mt-auto">
        <span className="text-hxd-subtle">
          {row.articleCount} {row.articleCount === 1 ? 'article' : 'articles'}
          {row.matchCount !== undefined && row.matchCount !== row.articleCount && (
            <> · {row.matchCount} match{row.matchCount === 1 ? '' : 'es'}</>
          )}
        </span>
        <span className="text-hxd-orange font-bold text-[12px]">Read →</span>
      </div>
    </Link>
  );
}

export default function ArchiveBrowser({ headerSlot = null, editionGridClass }) {
  const [view, setView] = useState('editions');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextSkip, setNextSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const debouncedQuery = useDebounced(query, 300);

  const generationRef = useRef(0);

  const filterKey = useMemo(
    () =>
      JSON.stringify({
        view,
        q: debouncedQuery.trim(),
        category,
        from,
        to,
      }),
    [view, debouncedQuery, category, from, to]
  );

  const buildParams = useCallback(
    (skip) => {
      const p = new URLSearchParams();
      p.set('view', view);
      if (debouncedQuery.trim()) p.set('q', debouncedQuery.trim());
      if (category) p.set('category', category);
      if (from) p.set('from', from);
      if (to) p.set('to', to);
      p.set('skip', String(skip));
      p.set('limit', String(PAGE_SIZE));
      return p;
    },
    [view, debouncedQuery, category, from, to]
  );

  useEffect(() => {
    const gen = ++generationRef.current;
    setRows([]);
    setHasMore(false);
    setNextSkip(0);
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    (async () => {
      try {
        const data = await api.get(`/archive/list?${buildParams(0).toString()}`);
        if (gen !== generationRef.current) return;
        setRows(data.rows || []);
        setHasMore(Boolean(data.hasMore));
        setNextSkip(data.nextSkip || 0);
      } catch (err) {
        if (gen !== generationRef.current) return;
        setError(err.message);
        setRows([]);
        setHasMore(false);
      } finally {
        if (gen === generationRef.current) setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || !hasMore) return;
    const gen = generationRef.current;
    setLoadingMore(true);
    try {
      const data = await api.get(`/archive/list?${buildParams(nextSkip).toString()}`);
      if (gen !== generationRef.current) return;
      setRows((prev) => [...prev, ...(data.rows || [])]);
      setHasMore(Boolean(data.hasMore));
      setNextSkip(data.nextSkip || nextSkip);
    } catch (err) {
      if (gen !== generationRef.current) return;
      setError(err.message);
      setHasMore(false);
    } finally {
      if (gen === generationRef.current) setLoadingMore(false);
    }
  }, [loadingMore, loading, hasMore, nextSkip, buildParams]);

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    // Pick whichever scroll container actually wraps us — viewport on the
    // standalone route page, modal body when embedded in the editor.
    const root = findScrollParent(el);
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { root, rootMargin: '600px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const hasActiveFilters =
    debouncedQuery.trim() || category || from || to;

  const clearFilters = () => {
    setQuery('');
    setCategory('');
    setFrom('');
    setTo('');
  };

  const switchView = (nextView) => {
    if (nextView === view) return;
    generationRef.current += 1;
    setRows([]);
    setHasMore(false);
    setNextSkip(0);
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    setView(nextView);
  };

  const gridClass =
    editionGridClass ||
    'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4';

  const archiveInputClass =
    'w-full bg-white border border-[#D4D4D4] px-3 py-2 text-[13px] text-[#0A0A0A] ' +
    'placeholder:text-[#9A9A9A] focus:outline-none focus:border-black transition-colors';

  return (
    <>
      <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
        <div className="min-w-0">{headerSlot}</div>

        <div className="inline-flex border border-black bg-white overflow-hidden shrink-0">
          <button
            type="button"
            onClick={() => switchView('articles')}
            className={[
              'px-4 py-1.5 font-mono text-[11px] font-bold tracking-[0.14em] uppercase transition-colors',
              view === 'articles'
                ? 'bg-black text-white'
                : 'bg-white text-[#0A0A0A] hover:bg-[#FAFAFA]',
            ].join(' ')}
          >
            Articles
          </button>
          <button
            type="button"
            onClick={() => switchView('editions')}
            className={[
              'px-4 py-1.5 font-mono text-[11px] font-bold tracking-[0.14em] uppercase transition-colors border-l border-black',
              view === 'editions'
                ? 'bg-black text-white'
                : 'bg-white text-[#0A0A0A] hover:bg-[#FAFAFA]',
            ].join(' ')}
          >
            Editions
          </button>
        </div>
      </div>

      {/* Filter panel — matches BriefArticleSidebar surface language */}
      <div className="border border-black bg-white mb-5">
        <div className="px-4 py-2.5 border-b border-black bg-[#FAFAFA] flex items-center justify-between gap-3">
          <div className="font-mono text-[10px] font-bold tracking-[0.22em] uppercase text-[#737373]">
            Filters
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="font-mono text-[10px] font-bold tracking-[0.14em] uppercase text-hxd-orange hover:text-black transition-colors"
            >
              Clear all
            </button>
          )}
        </div>

        <div className="p-4 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
          <div className="min-w-0">
            <label
              htmlFor="archive-search"
              className="block font-mono text-[10px] font-bold tracking-[0.16em] uppercase text-[#737373] mb-1.5"
            >
              Search
            </label>
            <input
              id="archive-search"
              type="search"
              className={archiveInputClass}
              placeholder="Headlines, summaries, takeaways…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div>
            <label
              htmlFor="archive-category"
              className="block font-mono text-[10px] font-bold tracking-[0.16em] uppercase text-[#737373] mb-1.5"
            >
              Category
            </label>
            <select
              id="archive-category"
              className={archiveInputClass + ' pr-8 appearance-none cursor-pointer'}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">All</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="archive-from"
              className="block font-mono text-[10px] font-bold tracking-[0.16em] uppercase text-[#737373] mb-1.5"
            >
              From
            </label>
            <input
              id="archive-from"
              type="date"
              className={archiveInputClass + ' cursor-pointer'}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              aria-label="From date"
            />
          </div>

          <div>
            <label
              htmlFor="archive-to"
              className="block font-mono text-[10px] font-bold tracking-[0.16em] uppercase text-[#737373] mb-1.5"
            >
              To
            </label>
            <input
              id="archive-to"
              type="date"
              className={archiveInputClass + ' cursor-pointer'}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              aria-label="To date"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4 px-1">
        <span className="font-mono text-[11px] font-bold tracking-[0.14em] uppercase text-[#525252]">
          {loading
            ? 'Searching…'
            : `${rows.length}${hasMore ? '+' : ''} ${
                view === 'articles' ? 'article' : 'edition'
              }${rows.length === 1 ? '' : 's'}`}
        </span>
        {loadingMore && (
          <span className="font-mono text-[11px] text-[#737373]">Loading more…</span>
        )}
      </div>

      {error && (
        <div className="text-[13px] text-hxd-err mb-4">Couldn&apos;t load archive: {error}</div>
      )}

      {!loading && rows.length === 0 && !error && (
        <div className="text-[13px] text-hxd-muted italic py-12 text-center border border-dashed border-hxd-border rounded">
          No matches. Try a broader search or clear some filters.
        </div>
      )}

      {view === 'articles' ? (
        <div className="space-y-2">
          {rows.map((r) => (
            <ArticleRow key={`${r.isoDate}-${r.articleId}`} row={r} />
          ))}
        </div>
      ) : (
        <div className={gridClass}>
          {rows.map((r) => (
            <EditionCard key={r.isoDate} row={r} />
          ))}
        </div>
      )}

      <div ref={sentinelRef} className="h-12" aria-hidden="true" />

      {!hasMore && rows.length > 0 && !loading && (
        <div className="text-center text-[11px] text-hxd-muted mt-4 mb-2 uppercase tracking-display-sm">
          End of archive
        </div>
      )}
    </>
  );
}
